import DashboardLayout from '../../layouts/DashoardLayout';
import { Box, Stack, Tab, Tabs, Typography } from '../../ui/core';
import api from '../../lib/api';
import useSdpFetch from './useSdpFetch';
import useQueryState, { oneOf, useSetQueryParams } from './useQueryState';
import StatusTab from './StatusTab';
import GuardsTab from './GuardsTab';
import StateTab from './StateTab';
import ReadoutTab from './ReadoutTab';
import PolicyChangesTab from './PolicyChangesTab';

// HITCH-5500: one place to see and steer Supply-Demand Pace (SDP). Ownership
// lives in the GrowthBook `sdp.config` flag (shown read-only on Status); the
// writes here are guard clears, manual policy changes and reference resets.
// Scaffold follows pages/TripThrottling: a plain page header over stacked
// cards, composed by hand inside the flush layout.
const pageSx = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  p: '12px',
  minHeight: '100vh',
  overflowY: 'auto',
};

const TABS = [
  { value: 'status', label: 'Status' },
  { value: 'guards', label: 'Guards' },
  { value: 'state', label: 'State' },
  { value: 'readout', label: 'Readout' },
  { value: 'policy', label: 'Policy changes' },
];

const DEFAULT_TAB = TABS[0].value;

const SupplyDemandPaceShell = () => {
  // The tab and its filters live in the URL. A tab change is pushed (back
  // returns to the previous tab) and drops the tab being left's filters, so
  // every tab opens on its defaults and a link carries one tab's view.
  const [tab] = useQueryState('tab', { defaultValue: DEFAULT_TAB, parse: oneOf(TABS.map((t) => t.value)) });
  const setParams = useSetQueryParams();
  const setTab = (value) => setParams({ tab: value === DEFAULT_TAB ? null : value }, { push: true, reset: true });
  // Status feeds the Status tab and the city pickers on Guards / State / Policy changes.
  const status = useSdpFetch(() => api.supplyDemandPace.getStatus(), 'status');
  const cities = (status.data && status.data.cities) || [];

  return (
    <Box sx={pageSx}>
      <Stack spacing="16px" sx={{ maxWidth: 1280, width: '100%' }}>
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 600, letterSpacing: '-0.01em' }}>Supply-Demand Pace</Typography>
          <Typography color="text.secondary" sx={{ fontSize: '13px', mt: '4px' }}>
            Driver pay controller. Ownership is the GrowthBook sdp.config flag; only active cities are paid by SDP.
          </Typography>
        </Box>
        <Tabs value={tab} onChange={(_e, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
          {TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>
        {tab === 'status' && (
          <StatusTab status={status.data} loading={status.loading} error={status.error} onRefresh={status.refetch} />
        )}
        {tab === 'guards' && <GuardsTab cities={cities} />}
        {tab === 'state' && <StateTab cities={cities} />}
        {tab === 'readout' && <ReadoutTab />}
        {tab === 'policy' && <PolicyChangesTab cities={cities} onChanged={status.refetch} />}
      </Stack>
    </Box>
  );
};

export { SupplyDemandPaceShell };

const SupplyDemandPace = () => (
  <DashboardLayout flush title="Supply-Demand Pace">
    <SupplyDemandPaceShell />
  </DashboardLayout>
);

export default SupplyDemandPace;
